import { z } from "zod";
import { EMBED_PROVIDERS, embedSource, linkSchema, richTextSchema } from "./blocks";

/*
 * Sermons and media (ADR 0061): what the database hands the public site, the portal and the podcast feed about a
 * church's published sermons, and the rules every surface shares (which video addresses are allowed, which audio
 * files, how an audio address is built). Nothing here reads the database; `get_public_sermons`,
 * `get_public_sermon_feed` and the page envelope's `sermons` part do (supabase/migrations/20261011130000_*).
 */

// ------------------------------------------------------------------------------------------------ media rules

/** The video providers a sermon may use: the embed block's YouTube and Vimeo (ADR 0033), never an arbitrary frame. */
export const SERMON_VIDEO_PROVIDERS = ["youtube", "vimeo"] as const;
export type SermonVideoProvider = (typeof SERMON_VIDEO_PROVIDERS)[number];

/** The provider a video address belongs to, or null when it is not one HoW plays. Mirrors private.sermon_video_provider. */
export function sermonVideoProvider(url: string): SermonVideoProvider | null {
  for (const provider of SERMON_VIDEO_PROVIDERS)
    if (EMBED_PROVIDERS[provider].test(url)) return provider;
  return null;
}

/** The frame address a sermon's video plays in, built from the page address the church pasted (ADR 0033). */
export function sermonVideoEmbed(video: { provider: SermonVideoProvider; url: string }): string {
  return embedSource(video.provider, video.url);
}

/** Audio a church may upload: MP3, AAC or M4A, as Planning Center accepts, up to 50 MB (the how-audio bucket's cap). */
export const SERMON_AUDIO_TYPES = ["audio/mpeg", "audio/mp4", "audio/x-m4a", "audio/aac"] as const;
export const MAX_SERMON_AUDIO_BYTES = 50 * 1024 * 1024;
/** The Storage bucket uploads go to (`<church id>/sermons/<file>`). */
export const SERMON_AUDIO_BUCKET = "how-audio";

/** The public address of an uploaded sermon's audio file. */
export function sermonAudioFileUrl(supabaseUrl: string, path: string): string {
  const encoded = path.split("/").map(encodeURIComponent).join("/");
  return `${supabaseUrl.replace(/\/$/, "")}/storage/v1/object/public/${SERMON_AUDIO_BUCKET}/${encoded}`;
}

/** "Sunday 4 October 2026" in the church's locale. A sermon's date is a calendar date, not an instant. */
export function formatSermonDate(isoDate: string, locale = "en-US"): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined || Number.isNaN(y + m + d))
    return isoDate;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** "42 min" or "1 h 5 min", for a duration in seconds. */
export function formatDuration(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${String(minutes)} min`;
  const rest = minutes % 60;
  return rest === 0
    ? `${String(Math.floor(minutes / 60))} h`
    : `${String(Math.floor(minutes / 60))} h ${String(rest)} min`;
}

// ------------------------------------------------------------------------------------------------ what visitors see

const artworkSchema = z.object({
  path: z.string().min(1).max(500),
  alt: z.string().max(200).default(""),
});

/** A published sermon as a card: what a list, a block or a series page shows. */
export const publicSermonSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  /** YYYY-MM-DD. */
  preachedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  summary: z.string().optional(),
  speaker: z
    .object({ id: z.string().min(1), name: z.string().min(1), role: z.string().optional() })
    .optional(),
  series: z
    .object({ id: z.string().min(1), slug: z.string().min(1), title: z.string().min(1) })
    .optional(),
  scripture: z.array(z.string()).max(10).default([]),
  video: z
    .object({ provider: z.enum(SERMON_VIDEO_PROVIDERS), url: z.string().min(1) })
    .refine((video) => EMBED_PROVIDERS[video.provider].test(video.url), {
      message: "not an address this provider serves",
    })
    .optional(),
  hasAudio: z.boolean().default(false),
  artwork: artworkSchema.optional(),
});
export type PublicSermon = z.infer<typeof publicSermonSchema>;

/** A published sermon in full, for its own page, the portal and the podcast. */
export const publicSermonDetailSchema = publicSermonSchema.extend({
  /** Structured rich text (ADR 0033); a sermon whose notes do not parse is drawn without them. */
  notes: z.unknown().optional(),
  resources: z.array(linkSchema).max(20).default([]),
  audio: z
    .object({
      path: z.string().min(1).optional(),
      url: z.url({ protocol: /^https$/ }).optional(),
      mime: z.string().optional(),
      bytes: z.number().int().positive().optional(),
      durationSeconds: z.number().int().positive().optional(),
    })
    .refine((audio) => (audio.path === undefined) !== (audio.url === undefined), {
      message: "audio is a file or a link",
    })
    .optional(),
  campus: z.object({ name: z.string().min(1), slug: z.string().min(1) }).optional(),
});
export type PublicSermonDetail = z.infer<typeof publicSermonDetailSchema>;

/** A sermon's notes as rich text, or null when it has none or they do not parse. */
export function sermonNotes(sermon: Pick<PublicSermonDetail, "notes">) {
  const parsed = richTextSchema.safeParse(sermon.notes);
  return parsed.success ? parsed.data : null;
}

/** A series with at least one published sermon. */
export const publicSermonSeriesSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional(),
  artwork: artworkSchema.optional(),
  startsOn: z.string().optional(),
  endsOn: z.string().optional(),
  sermonCount: z.number().int().nonnegative().default(0),
  latestOn: z.string().optional(),
});
export type PublicSermonSeries = z.infer<typeof publicSermonSeriesSchema>;

/** Parses a list one item at a time: an item that fails its contract is left out, not the list. */
export function parseEach<T>(schema: z.ZodType<T>, items: readonly unknown[]): T[] {
  const kept: T[] = [];
  for (const item of items) {
    const parsed = schema.safeParse(item);
    if (parsed.success) kept.push(parsed.data);
  }
  return kept;
}

/** The sermon feature block's sermons, in the order it draws them (newest first), from the page's `sermons` part. */
export function sermonsForFeature(
  source:
    | { mode: "latest" }
    | { mode: "series"; seriesId: string }
    | { mode: "sermon"; sermonId: string },
  sermons: readonly PublicSermon[],
  limit: number,
): PublicSermon[] {
  const newest = [...sermons].sort((a, b) => b.preachedOn.localeCompare(a.preachedOn));
  switch (source.mode) {
    case "latest":
      return newest.slice(0, limit);
    case "series":
      return newest.filter((s) => s.series?.id === source.seriesId).slice(0, limit);
    case "sermon":
      return newest.filter((s) => s.id === source.sermonId).slice(0, 1);
  }
}

// ------------------------------------------------------------------------------------------------ the /sermons pages

const archiveViewSchema = z.object({
  kind: z.literal("archive"),
  page: z.number().int().positive(),
  pageCount: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  speaker: z.object({ id: z.string(), name: z.string() }).nullish(),
  speakers: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
  sermons: z.array(z.unknown()).default([]),
  series: z.array(z.unknown()).default([]),
});

const seriesViewSchema = z.object({
  kind: z.literal("series"),
  series: z.unknown(),
  sermons: z.array(z.unknown()).default([]),
});

const sermonViewSchema = z.object({
  kind: z.literal("sermon"),
  sermon: z.unknown(),
  more: z.array(z.unknown()).default([]),
});

export type SermonView =
  | {
      readonly kind: "archive";
      readonly page: number;
      readonly pageCount: number;
      readonly total: number;
      readonly speaker: { readonly id: string; readonly name: string } | null;
      readonly speakers: readonly { readonly id: string; readonly name: string }[];
      readonly sermons: readonly PublicSermon[];
      readonly series: readonly PublicSermonSeries[];
    }
  | {
      readonly kind: "series";
      readonly series: PublicSermonSeries;
      readonly sermons: readonly PublicSermon[];
    }
  | {
      readonly kind: "sermon";
      readonly sermon: PublicSermonDetail;
      readonly more: readonly PublicSermon[];
    };

/** What `get_public_sermons` adds to the site's name, brand and menus: the view, or null for nothing published there. */
export interface SermonPages {
  readonly view: SermonView | null;
  /** Whether the church's podcast is on, so the archive can offer its feed. */
  readonly podcast: boolean;
}

/** Parses `sermonView` and `podcast` from `get_public_sermons`; a view that does not parse is "nothing here". */
export function parseSermonPages(input: unknown): SermonPages {
  const raw = typeof input === "object" && input !== null ? (input as Record<string, unknown>) : {};
  const podcast = raw["podcast"] === true;
  const view = raw["sermonView"];
  const archive = archiveViewSchema.safeParse(view);
  if (archive.success) {
    return {
      podcast,
      view: {
        ...archive.data,
        speaker: archive.data.speaker ?? null,
        sermons: parseEach(publicSermonSchema, archive.data.sermons),
        series: parseEach(publicSermonSeriesSchema, archive.data.series),
      },
    };
  }
  const series = seriesViewSchema.safeParse(view);
  if (series.success) {
    const one = publicSermonSeriesSchema.safeParse(series.data.series);
    return {
      podcast,
      view: one.success
        ? {
            kind: "series",
            series: one.data,
            sermons: parseEach(publicSermonSchema, series.data.sermons),
          }
        : null,
    };
  }
  const sermon = sermonViewSchema.safeParse(view);
  if (sermon.success) {
    const one = publicSermonDetailSchema.safeParse(sermon.data.sermon);
    return {
      podcast,
      view: one.success
        ? {
            kind: "sermon",
            sermon: one.data,
            more: parseEach(publicSermonSchema, sermon.data.more),
          }
        : null,
    };
  }
  return { podcast, view: null };
}

/** The address of a sermon, a series, the archive (page n, one speaker) and the feed on a church's site. */
export const sermonPaths = {
  archive: (options: { page?: number; speakerId?: string } = {}): string => {
    const params = new URLSearchParams();
    if (options.speakerId !== undefined) params.set("speaker", options.speakerId);
    if (options.page !== undefined && options.page > 1) params.set("page", String(options.page));
    const query = params.toString();
    return query === "" ? "/sermons" : `/sermons?${query}`;
  },
  series: (slug: string): string => `/sermons/series/${slug}`,
  sermon: (slug: string): string => `/sermons/${slug}`,
  feed: "/sermons/feed.xml",
} as const;

/** Whether a request path is one the sermon pages answer (the feed has its own route). */
export function isSermonPath(path: string): boolean {
  return path === "sermons" || (path.startsWith("sermons/") && path !== "sermons/feed.xml");
}

// ------------------------------------------------------------------------------------------------ the podcast

const feedSchema = z.object({
  site: z.object({
    name: z.string().min(1),
    slug: z.string().min(1),
    locale: z.string().optional(),
  }),
  podcast: z.object({
    title: z.string().min(1),
    description: z.string().optional(),
    author: z.string().optional(),
    email: z.string().optional(),
    artworkPath: z.string().optional(),
    category: z.string().default("Religion & Spirituality"),
    explicit: z.boolean().default(false),
  }),
  episodes: z.array(z.unknown()).default([]),
});

export interface PodcastFeed {
  readonly site: z.infer<typeof feedSchema>["site"];
  readonly podcast: z.infer<typeof feedSchema>["podcast"];
  readonly episodes: readonly (PublicSermonDetail & {
    readonly publishedAt?: string | undefined;
  })[];
}

/** Parses `get_public_sermon_feed`; episodes are parsed one at a time and an episode without audio is left out. */
export function parsePodcastFeed(input: unknown): PodcastFeed {
  const feed = feedSchema.parse(input);
  const episodeSchema = publicSermonDetailSchema.extend({ publishedAt: z.string().optional() });
  return {
    site: feed.site,
    podcast: feed.podcast,
    episodes: parseEach(episodeSchema, feed.episodes).filter((e) => e.audio !== undefined),
  };
}

function xml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** "Sun, 04 Oct 2026 12:00:00 GMT": RSS's date, for a sermon preached on a calendar date (noon UTC that day). */
function rssDate(isoDate: string): string {
  return new Date(`${isoDate}T12:00:00Z`).toUTCString();
}

/** "00:42:07", the duration Apple Podcasts reads. */
function itunesDuration(seconds: number): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
}

/** Apple's category and, for Christianity and the others, the parent it sits under. */
function itunesCategory(category: string): string {
  if (category === "Religion & Spirituality") return `<itunes:category text="${xml(category)}"/>`;
  return `<itunes:category text="Religion &amp; Spirituality"><itunes:category text="${xml(category)}"/></itunes:category>`;
}

export interface FeedAddresses {
  /** The church's site, `https://<host>`. */
  readonly siteUrl: string;
  /** Where Storage serves public files from (SUPABASE_URL). */
  readonly supabaseUrl: string;
}

/**
 * The RSS 2.0 podcast feed, with the iTunes and Podcasting 2.0 tags Apple Podcasts and Spotify read (ADR 0061). One
 * item per published sermon with audio; the enclosure is the uploaded file or the church's own audio link. Every
 * value is escaped: nothing a church typed can open an element.
 */
export function buildPodcastFeed(feed: PodcastFeed, at: FeedAddresses): string {
  const site = at.siteUrl.replace(/\/$/, "");
  const p = feed.podcast;
  const image = p.artworkPath === undefined ? null : sermonImageUrl(at.supabaseUrl, p.artworkPath);
  const language = (feed.site.locale ?? "en-US").toLowerCase();
  const author = p.author ?? feed.site.name;
  const items = feed.episodes.map((e) => {
    const audio = e.audio;
    const enclosure =
      audio?.path !== undefined
        ? sermonAudioFileUrl(at.supabaseUrl, audio.path)
        : (audio?.url ?? "");
    const description = [
      e.summary,
      e.scripture.length > 0 ? `Scripture: ${e.scripture.join("; ")}` : undefined,
    ]
      .filter((x): x is string => x !== undefined)
      .join("\n\n");
    return [
      "<item>",
      `<title>${xml(e.title)}</title>`,
      `<link>${xml(`${site}${sermonPaths.sermon(e.slug)}`)}</link>`,
      `<guid isPermaLink="false">${xml(e.id)}</guid>`,
      `<pubDate>${rssDate(e.preachedOn)}</pubDate>`,
      description === "" ? "" : `<description>${xml(description)}</description>`,
      e.speaker === undefined ? "" : `<itunes:author>${xml(e.speaker.name)}</itunes:author>`,
      `<enclosure url="${xml(enclosure)}" length="${String(audio?.bytes ?? 0)}" type="${xml(audio?.mime ?? "audio/mpeg")}"/>`,
      audio?.durationSeconds === undefined
        ? ""
        : `<itunes:duration>${itunesDuration(audio.durationSeconds)}</itunes:duration>`,
      e.artwork === undefined
        ? ""
        : `<itunes:image href="${xml(sermonImageUrl(at.supabaseUrl, e.artwork.path))}"/>`,
      `<itunes:explicit>${p.explicit ? "true" : "false"}</itunes:explicit>`,
      "</item>",
    ]
      .filter((line) => line !== "")
      .join("");
  });
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<rss version="2.0" xmlns:itunes="http://www.itunes.com/dtds/podcast-1.0.dtd" xmlns:podcast="https://podcastindex.org/namespace/1.0" xmlns:atom="http://www.w3.org/2005/Atom">',
    "<channel>",
    `<title>${xml(p.title)}</title>`,
    `<link>${xml(`${site}${sermonPaths.archive()}`)}</link>`,
    `<atom:link href="${xml(`${site}${sermonPaths.feed}`)}" rel="self" type="application/rss+xml"/>`,
    `<language>${xml(language)}</language>`,
    `<description>${xml(p.description ?? `Sermons from ${feed.site.name}.`)}</description>`,
    `<itunes:author>${xml(author)}</itunes:author>`,
    p.email === undefined
      ? ""
      : `<itunes:owner><itunes:name>${xml(author)}</itunes:name><itunes:email>${xml(p.email)}</itunes:email></itunes:owner>`,
    image === null ? "" : `<itunes:image href="${xml(image)}"/>`,
    image === null
      ? ""
      : `<image><url>${xml(image)}</url><title>${xml(p.title)}</title><link>${xml(`${site}${sermonPaths.archive()}`)}</link></image>`,
    itunesCategory(p.category),
    `<itunes:explicit>${p.explicit ? "true" : "false"}</itunes:explicit>`,
    "<itunes:type>episodic</itunes:type>",
    ...items,
    "</channel>",
    "</rss>",
  ]
    .filter((line) => line !== "")
    .join("\n");
}

/** An image in the church's public media area (how-public). */
function sermonImageUrl(supabaseUrl: string, path: string): string {
  const encoded = path.split("/").map(encodeURIComponent).join("/");
  return `${supabaseUrl.replace(/\/$/, "")}/storage/v1/object/public/how-public/${encoded}`;
}

// ------------------------------------------------------------------------------------------------ examples

/** Sermons for the sermon feature block's example and the theme conformance test. */
export const EXAMPLE_SERMONS: readonly PublicSermon[] = [
  {
    id: "7d2c1b3a-4e5f-4a6b-8c7d-9e0f1a2b3c4d",
    slug: "anchored-in-hope",
    title: "Anchored in hope",
    preachedOn: "2026-10-04",
    summary: "Hope that holds when the water rises.",
    speaker: {
      id: "1c2d3e4f-5a6b-4c7d-8e9f-0a1b2c3d4e5f",
      name: "Pastor Ada Obi",
      role: "Lead pastor",
    },
    series: { id: "4a1b2c3d-5e6f-4a7b-8c9d-0e1f2a3b4c5d", slug: "hope", title: "Hope" },
    scripture: ["Hebrews 6:13-20"],
    video: { provider: "youtube", url: "https://www.youtube.com/watch?v=abcdefghijk" },
    hasAudio: true,
  },
  {
    id: "8e3d2c4b-5f60-4b7c-9d8e-0f1a2b3c4d5e",
    slug: "a-living-hope",
    title: "A living hope",
    preachedOn: "2026-09-27",
    speaker: { id: "2d3e4f5a-6b7c-4d8e-9f0a-1b2c3d4e5f6a", name: "Sam Rivera" },
    series: { id: "4a1b2c3d-5e6f-4a7b-8c9d-0e1f2a3b4c5d", slug: "hope", title: "Hope" },
    scripture: ["1 Peter 1:3-9"],
    hasAudio: false,
  },
];

export const EXAMPLE_SERMON_SERIES: readonly PublicSermonSeries[] = [
  {
    id: "4a1b2c3d-5e6f-4a7b-8c9d-0e1f2a3b4c5d",
    slug: "hope",
    title: "Hope",
    description: "Six weeks on the hope that anchors us.",
    sermonCount: 2,
    latestOn: "2026-10-04",
  },
];
